import gql from 'graphql-tag';
import { useQuery, provideApolloClient } from '@vue/apollo-composable';
import { watch, reactive } from 'vue';
import useAlerts from '@/composables/useAlerts';
import useNotifications from '@/composables/useNotifications';
import apolloClient from './useApolloClient';
import useDecryptSymbols from './useDecryptSymbols';

const { addAlert } = useAlerts();
const { addNotification, notifications, removeNotification } = useNotifications();
const { decryptSymbols } = useDecryptSymbols();

const GET_LOGGED_IN_USER = gql`
  query LoggedInUser {
    loggedInUser {
      BACKINOFFICE
      FIRSTNAME
      LASTNAME
      DISPLAYNAME
      EMAIL
      INSIDESALES
      OOO
      OUTSIDESALES
      ADMIN
      LEADSADMIN
      CONTACTSADMIN
      SHOWQUOTES
      QUOTESADMIN
      SHOWLEADS
      SHOWIMPACTREPORT
      SHOWCONTACTS
      SHOWGENERALREPORTS
      STARTPAGE
      USERID
    }
  }
`;

const user = reactive({
  userid: null,
  firstName: '',
  lastName: '',
  displayName: '',
  email: '',
  startPage: '',
  admin: false,
  showLeads: false,
  outOfOffice: false,
  backInOffice: null,
  leadsAdmin: false,
  contactsAdmin: false,
  showQuotes: false,
  quotesAdmin: false,
  showImpactReport: false,
  showContacts: false,
  showGeneralReports: false,
  insideSales: false,
  outsideSales: false,
});

export default function useCurrentUser() {
  function getCurrentUser() {
    provideApolloClient(apolloClient);

    const { result, error, loading } = useQuery(GET_LOGGED_IN_USER);

    watch(error, () => {
      if (error.value) {
        console.error('Error getting current user information:', error.value);
        addAlert('Error getting current user information', 'high', 4);
      }
    });

    watch(
      result,
      () => {
        if (result.value?.loggedInUser) {
          user.userid = result.value.loggedInUser.USERID;
          user.firstName = decryptSymbols(result.value.loggedInUser.FIRSTNAME);
          user.lastName = decryptSymbols(result.value.loggedInUser.LASTNAME);
          user.displayName = decryptSymbols(result.value.loggedInUser.DISPLAYNAME);
          user.email = decryptSymbols(result.value.loggedInUser.EMAIL);
          user.startPage = result.value.loggedInUser.STARTPAGE;
          user.admin = result.value.loggedInUser.ADMIN;
          user.showLeads = result.value.loggedInUser.SHOWLEADS;
          user.outOfOffice = result.value.loggedInUser.OOO;
          user.backInOffice = result.value.loggedInUser.BACKINOFFICE;
          user.leadsAdmin = result.value.loggedInUser.LEADSADMIN;
          user.contactsAdmin = result.value.loggedInUser.CONTACTSADMIN;
          user.showQuotes = result.value.loggedInUser.SHOWQUOTES;
          user.quotesAdmin = result.value.loggedInUser.QUOTESADMIN;
          user.showImpactReport = result.value.loggedInUser.SHOWIMPACTREPORT;
          user.showContacts = result.value.loggedInUser.SHOWCONTACTS;
          user.showGeneralReports = result.value.loggedInUser.SHOWGENERALREPORTS;
          user.insideSales = result.value.loggedInUser.INSIDESALES;
          user.outsideSales = result.value.loggedInUser.OUTSIDESALES;

          const notificationText = `$$OOO`;
          notifications.forEach((notification) => {
            if (notification.message === notificationText) {
              removeNotification(notification.id);
            }
          });

          if (result.value.loggedInUser.OOO) {
            addNotification(notificationText, 'high');
          }
        }
      },
      { deep: true }
    );

    return { loading, user, error };
  }

  return {
    user,
    getCurrentUser,
  };
}
